import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Store } from '@ngrx/store';
import { tap, withLatestFrom, map, catchError, switchMap, concatMap } from 'rxjs/operators';
import { of } from 'rxjs';

import * as fromDrawerState from './drawer-state.reducer';
import * as DrawerStateActions from './drawer-state.actions';
import * as DrawerStateSelectors from './drawer-state.selectors';
import { DrawerService } from '../../services/drawer/drawer.service';

@Injectable()
export class DrawerStateEffects {
  openDrawer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DrawerStateActions.openDrawer),
        switchMap(action =>
          of(action).pipe(
            withLatestFrom(this.store.select(DrawerStateSelectors.getDrawers))
          )
        ),
        tap(([action, data]) => {
          const drawers = [...data];
          const lastDrawer = drawers.splice(-1, 1)[0];
          if (lastDrawer !== action.params.name) {
            this.drawerService.openDrawer(action.params);
          }
        })
      ),
    { dispatch: false }
  );

  closeDrawer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DrawerStateActions.closeDrawer),
      concatMap(action =>
        of(action).pipe(
          withLatestFrom(this.store.select(DrawerStateSelectors.getDrawers))
        )
      ),
      map(([action, data]) => {
        const drawers = [...data];
        const { name: drawerName } = action.params;
        const lastDrawer = drawers.splice(-1, 1)[0];
        if (lastDrawer !== drawerName) {
          this.notification.error('Error', 'Error while closing');
          return DrawerStateActions.closeDrawerFailure({ error: 'Error while closing' });
        } else {
          return DrawerStateActions.closeDrawerSuccess({ popped: action.params, drawers });
        }
      }),
      catchError(error => of(DrawerStateActions.closeDrawerFailure({ error })))
    )
  );

  closeDrawerSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DrawerStateActions.closeDrawerSuccess),
        tap(action => {
          action.popped?.onClose?.(action.popped?.props);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private notification: NzNotificationService,
    private drawerService: DrawerService,
    private store: Store<fromDrawerState.DrawerStatePartialState>
  ) {}
}
